<template>
    <Row type="none" class="bg-white rounded shadow min-h-screen">
        <div v-if="canManageEmployees()" class="container pt-4">
            <ButtonTag :is-tw-main-btn="true" url="employees/add" class="w-44" :is-w100="false">
                <div class="pt-1">{{ $t('add') }}</div>
            </ButtonTag>
            <ButtonTag :is-tw-main-btn="true" url="employees-at-work" class="w-44 mx-3" :is-w100="false">
                <div class="pt-1">{{ $t('atWork.title') }}</div>
            </ButtonTag>
            <ButtonTag :is-tw-main-btn="true" url="employees-salary" class="w-44 " :is-w100="false">
                <div class="pt-1">{{ $t('kpi.salary.mainTitle') }}</div>
            </ButtonTag>
        </div>

        <div class="container">
            <SearchCollapse :is-bg-white="true">
                <div class="grid grid-cols-12 gap-3">
                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                            {{ $t('person.givenName') }}
                        </div>
                        <b-form-input
                            v-model="search.employeeGivenName"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchEmployees"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                            {{ $t('person.familyName') }}
                        </div>
                        <b-form-input
                            v-model="search.employeeFamilyName"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchEmployees"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                            {{ $t('person.telephone') }}
                        </div>
                        <b-form-input
                            v-model="search.employeeTelephone"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchEmployees"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3">
                        <button class="bg-main border-0 hover:bg-hoverMain rounded-l h-11 w-50 focus:outline-none">
                            <img src="/images/search1.png" alt="" class="h-6 cursor-pointer" @click="searchEmployees">
                        </button>
                        <button class="bg-main border-0 hover:bg-hoverMain rounded-r h-11 w-50 focus:outline-none">
                            <img src="/images/eraser%20(2).png" alt="" class="h-6 cursor-pointer" @click="resetEmployeeSearchInfo">
                        </button>
                    </div>
                </div>
            </SearchCollapse>
        </div>

        <!--  desktop version  -->
        <table class="w-full">
            <thead class="h-11 bg-lightGray">
            <tr>
                <th class="d-none d-md-table-cell pl-3">{{$t('id')}}</th>
                <th class="pl-3">{{ $t('name') }}</th>
                <th>{{ $t('employee.position') }}</th>
                <th class="d-none d-md-table-cell">{{ $t('person.telephone') }}</th>
                <th class="d-none d-md-table-cell">{{ $t('employee.hiredDate') }}</th>
                <th>{{ $t('employee.salary') }}</th>
                <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(employee, index ) of getEmployees" :key="index" class="h-11 border-bottom">
                <td class="d-none d-md-table-cell pl-3">
                    {{ employee.id }}
                </td>
                <td class="pl-3">
                    <div class="d-flex align-items-center text-main">
                        <img
                            :src="employee.image ? url + employee.image.contentUrl : ''"
                            style="border-radius: 50%;"
                            height="34px"
                            alt=""
                            @click="setModal(employee.givenName, url + employee.image.contentUrl)"
                            class="d-none d-md-table-cell"
                        >
                        <router-link :to="$i18nHelper.getUrl('employee/' + employee.id)" class="d-none d-sm-block ml-2">
                            &ensp;{{ employee.givenName + ' ' + employee.familyName + ' ' + employee.additionalName }}
                        </router-link>
                        <router-link :to="$i18nHelper.getUrl('employee/' + employee.id)" class="d-block d-sm-none ml-1">
                            {{ employee.givenName }}
                        </router-link>
                    </div>
                </td>
                <td v-if="employee.employeePositions">
                    {{ employee.employeePositions[0].name}}
                </td>
                <td class="d-none d-md-table-cell">
                    {{ formatPhoneNumber(employee.telephone) }}
                </td>
                <td v-if="employee.employeeInformation" class="d-none d-md-table-cell">
                    {{ dateFormat(employee.employeeInformation.hiredDate, true) }}
                </td>
                <td v-else class="d-none d-md-table-cell">
                    -
                </td>
                <td v-if="employee.employeeInformation">
                    {{ showCurrencyById(employee.employeeInformation.currency.id, employee.employeeInformation.salary) }}
                </td>
                <td v-else> - </td>
                <td class="d-flex pr-3">
                    <img src="/images/edit.png" alt="" class="h-5 mt-1 cursor-pointer pr-3" @click="$i18nHelper.toUrl('employees/' + employee.id)">

                    <img src="/images/read-more.png" alt="" class="h-7 cursor-pointer pr-3" @click="$i18nHelper.toUrl('employee/' + employee.id)">

                    <img v-if="canManageEmployees()" src="/images/Union.png" alt="" class="h-6 mt-0.5 cursor-pointer" @click="$bvModal.show('delete-people-' + employee.id)">

                    <Modal
                        text="deleteConfirm"
                        ok-text="yes"
                        cancel-text="no"
                        :is-ok-only="false"
                        :context="{name: employee.givenName}"
                        :id="'delete-people-' + employee.id"
                        :on-ok-pressed="() => {modalOk(employee.id)}"
                    />
                </td>
            </tr>
            </tbody>
        </table>

        <ModalWithPicture id="employee_picture" :img="itemImageUrl" :title="employeeGivenName"/>

        <div class="container mt-4">
            <PaginationRow
                :search="search"
                base-url="employees"
                method-name="searchEmployee"
                :total-items="getEmployeesTotalCount"
            ></PaginationRow>
        </div>
    </Row>
</template>

<script>
import Row from "@/components/html/Row";
import ButtonTag from "@/components/html/ButtonTag";
import accessMixin from "@/mixin/accessMixin";
import PaginationRow from "@/components/PaginationRow";
import {mapActions, mapGetters} from "vuex";
import Modal from "@/components/html/Modal";
import SearchCollapse from "@/components/SearchCollapse";
import ModalWithPicture from "../../components/html/ModalWithPicture";
import validatorMixin from "@/mixin/validatorMixin";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";
import dateFormattingMixin from "@/mixin/dateFormattingMixin";
import currencyRateMixin from "@/mixin/currencyRateMixin";

export default {
    name: "NewList",
    components: {
        PaginationRow,
        ButtonTag,
        Row,
        Modal,
        SearchCollapse,
        ModalWithPicture,
    },
    mixins: [validatorMixin, accessMixin, currencyFormatMixin, dateFormattingMixin, currencyRateMixin],
    data() {
        return {
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'employee.title'},
            ],
            isLoading: false,
            employeeGivenName: '',
            itemImageUrl: '',
            url: process.env.VUE_APP_API_URL,
            search: {
                employeeGivenName: '',
                employeeFamilyName: '',
                employeeTelephone: '',
                page: 1
            },
        }
    },
    computed: {
        ...mapGetters(['getEmployeesTotalCount', 'getEmployees'])
    },
    methods: {
        ...mapActions(['deleteEmployee', 'fetchEmployees', 'searchEmployee', 'setBreadcrumbItems']),
        setModal(givenName, url) {
            this.employeeGivenName = givenName
            this.itemImageUrl = url
            this.$bvModal.show('employee_picture')
        },
        formatPhoneNumber(number) {
            if (number) {
                return number.replace(/\D+/g, '').replace(/(\d{3})(\d{2})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
            }
        },
        modalOk(id) {
            this.deleteEmployee(id)
                .then(() => {
                    this.fetchEmployees()
                })
        },
        getEmployeeFullName(item) {
            return item.givenName + ' ' + item.familyName + ' ' + item.additionalName
        },
        searchEmployees() {
            this.$root.showOverlay = true
            this.searchEmployee(this.search)
                .catch((err) => {
                    console.log(err)
                })
                .finally(() => {
                    this.$root.showOverlay = false
                });
        },
        resetEmployeeSearchInfo() {
            Object.assign(this.$data, this.$options.data.call(this));
            this.searchEmployees()
        }
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    }
}
</script>

<style scoped>
.form-control {
    margin-bottom: 5px !important;
}

.mt-3 {
    margin-top: 0 !important;
}

.col-3, .col-4, .col-1, .col-11 {
    padding: 2px !important;
    margin: 0 !important;
}

img {
    cursor: pointer;
}

@media (min-width: 576px) {
    .mr-md-1 {
        margin-right: 0.25rem !important;
    }
}
</style>